/********************************************************************
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this 
 * file in accordance with the terms of the Adobe license agreement 
 * accompanying it. 
 *******************************************************************/

import { HTMLAttributes } from 'preact/compat';
import { FunctionComponent, VNode } from 'preact';
import { VComponent, classes } from '@adobe-commerce/elsie/lib';
import '@adobe-commerce/elsie/components/Breadcrumbs/Breadcrumbs.css';

export interface BreadcrumbsProps extends HTMLAttributes<HTMLElement> {
  categories: VNode[];
  separator?: VNode<HTMLAttributes<SVGSVGElement>>;
}

export const Breadcrumbs: FunctionComponent<BreadcrumbsProps> = ({
  className,
  categories,
  separator,
  ...props
}) => {
  return (
    <>
      {categories?.length > 1 && (
        <nav
          role="navigation"
          {...props}
          className={classes(['dropin-breadcrumbs__container', className])}
        >
          <ul className="dropin-breadcrumbs__items">
            {categories?.map((category: VNode, index: number) => {
              return (
                <li
                  key={index}
                  className={classes([
                    'dropin-breadcrumbs__item',
                    [
                      'dropin-breadcrumbs__item--last',
                      index === categories.length - 1,
                    ],
                  ])}
                >
                  <VComponent
                    node={category}
                    className="dropin-breadcrumbs__link"
                  />
                  {!separator && index !== categories.length - 1 && (
                    <span className="dropin-breadcrumbs__separator--default">
                      {' '}
                      /{' '}
                    </span>
                  )}
                  {separator && index !== categories.length - 1 && (
                    <VComponent
                      node={separator}
                      className="dropin-breadcrumbs__separator--icon"
                    />
                  )}
                </li>
              );
            })}
          </ul>
        </nav>
      )}
    </>
  );
};
